import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';

/**
 * Lets the form page ask whichever collapsed section holds a field to open, so
 * a jump can actually land on it.
 *
 * The per-portal advertising subsections are collapsed by default and their
 * expand state is local to each FormLayoutComponent, so a field inside one is
 * not in the DOM at all — jumping to it scrolled nowhere and left no hint that
 * the section had to be opened by hand (BRIEFD-7094).
 *
 * Requests are both emitted (for sections already on screen) and remembered
 * (for sections that render afterwards — the advertising tab builds its
 * subsections asynchronously, so an emit alone is missed). `reset()` drops the
 * remembered set so one vehicle's gaps don't open sections on the next.
 */
/**
 * Well-known key travelling on this channel that is NOT a field uuid: the gap
 * helper asking the ad title/description flag tabs to select the market the
 * form is scoped to. The per-country description input lives inside those tabs,
 * so there is no uuid to point at until the right flag is active (BRIEFD-7326).
 */
export const FOCUS_SCOPED_COUNTRY_DESCRIPTION = 'country_description_missing';

@Injectable({providedIn: 'root'})
export class FormSectionExpandService {
  private readonly requests = new Subject<string>();
  private readonly pending = new Set<string>();

  /** Ask every section containing this field's uuid to expand. Only the latest
   *  request stays pending: a component mounting later (the tab the jump just
   *  switched to) should act on where the dealer is now, not on every step
   *  they walked through before. */
  expand(fieldUuid: string): void {
    this.pending.clear();
    this.pending.add(fieldUuid);
    this.requests.next(fieldUuid);
  }

  /** Whether a section rendering now should open itself for this field. */
  isPending(fieldUuid: string): boolean {
    return this.pending.has(fieldUuid);
  }

  /** Forget outstanding requests — call when a form starts loading. */
  reset(): void {
    this.pending.clear();
  }

  get expandRequests(): Observable<string> {
    return this.requests.asObservable();
  }
}
